// i18next-extract-mark-ns-start MyAccount
import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import { LoaderIcon } from 'react-hot-toast';
import withNavigation from '../../hoc/withNavigation';
import { navigate } from '../../services/navigation';
import { pushToLayer } from '../../services/googleTagManager';
import withOrderContext from '../../withOrderContext';
import withCustomerContext from '../../withCustomerContext';
import callApi from '../../services/api';
import routesMap from '../../Routes';
import OutInSideClick from '../home/v2/OutInSideClick';
import CustomerForm from '../order/CustomerForm';
import { tabletThresholdPixels, Button as ButtonV3 } from '../home/v3/styledComponents';
import { isEmailValid, isPhoneValid } from '../../services/checkingFormat';
import PopupLogin from './PopupLogin';
import { OpacityContainer, PopupContainer, StyledCloseIcon } from '../home/v4/styledComponents';
import closeIcon from '../../assets/order/knitTear/close.svg';

const Title = styled.div`
  font-size: 26px;
  font-weight: 500;
  margin-bottom: 26px;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 24px;
    margin-bottom: 22px;
  }
`;

const Button = styled(ButtonV3)`
  margin: 0px;
  width: 100%;
  margin-top: 5px;
  opacity: ${(props) => props.isActive ? 1 : 0.5};
  @media (max-width: ${tabletThresholdPixels}) {
    min-width: unset;
    width: 100%;
  }
`;

const Span = styled.span`
  margin: 10px 0px;
`;

function PopupTemporaryPassword({
  closePopup, orderContext: { country }, customerContext: { setCustomerAndGetOrders }, isFromOrderDetails, customerEmail, order,
}) {
  const { t } = useTranslation();

  const getEmailOrPhoneInput = () => ({
    emailOrPhone: {
      value: customerEmail || '', name: 'emailOrPhone', label: t('Adresse mail ou Téléphone'), isRequired: true, size: 'medium', isMobileLarge: true,
    },
    errorEmailOrPhone: { name: 'errorEmailOrPhone', isActive: false, label: t('Adresse mail ou numéro de téléphone non valide') },
  });

  const [isAlreadyCustomer, setIsAlreadyCustomer] = useState(true);
  const [emailOrPhoneForm, setEmailOrPhoneForm] = useState(getEmailOrPhoneInput());
  const [isTemporaryPasswordSent, setIsTemporaryPasswordSent] = useState(false);
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const checkFormInputValue = () => {
    const form = { ...emailOrPhoneForm };
    const { value } = form.emailOrPhone;
    const isEmail = isEmailValid(value);
    const isPhone = isPhoneValid(value, country);
    form.errorEmailOrPhone.isActive = !(isEmail || isPhone);
    form.emailOrPhone.error = !(isEmail || isPhone);
    if (form.errorEmailOrPhone.isActive) {
      form.errorEmailOrPhone.label = t('Adresse mail ou numéro de téléphone non valide');
    }
    setEmailOrPhoneForm({ ...form });
  };

  const setFormInputValue = (target) => {
    const { name, value } = target;
    const form = { ...emailOrPhoneForm };
    form[name].value = value;
    setEmailOrPhoneForm({ ...form });
  };

  const isEmailOrPhoneDone = () => {
    let hasError = false;
    if (emailOrPhoneForm.emailOrPhone.error !== false) {
      hasError = true;
      emailOrPhoneForm.errorEmailOrPhone.isActive = true;
    }
    if (hasError) setEmailOrPhoneForm({ ...emailOrPhoneForm });
    return !hasError;
  };

  const askForTemporaryPassword = () => {
    if (!customerEmail && !isEmailOrPhoneDone()) return;
    setIsLoading(true);
    const { value } = emailOrPhoneForm.emailOrPhone;
    const isEmail = isEmailValid(value);

    const callApiBody = {
      email: isEmail ? value.toLowerCase() : null,
      phone: isEmail ? null : value,
    };

    callApi('askForTemporaryPassword', 'post', callApiBody)
      .then(() => {
        setEmailOrPhone(value);
        setIsTemporaryPasswordSent(true);
      })
      .catch(() => {
        emailOrPhoneForm.errorEmailOrPhone.label = t('Email ou téléphone non reconnu');
        setIsAlreadyCustomer(!isAlreadyCustomer);
        emailOrPhoneForm.errorEmailOrPhone.isActive = true;
        emailOrPhoneForm.emailOrPhone.error = true;
        setEmailOrPhoneForm({ ...emailOrPhoneForm });
        setIsTemporaryPasswordSent(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      callApi('login', 'post', { googleAccessToken: tokenResponse.access_token })
        .then(({ customer }) => {
          pushToLayer({ event: 'LoginWithGoogle' });
          setCustomerAndGetOrders(customer);
          closePopup();
          if (![routesMap.Step1.url, routesMap.Step2.url, routesMap.Step3.url].includes(window.location.pathname)) {
            navigate(routesMap.MyAccount.url);
          }
        })
        .catch(() => {
          console.error('Google login failed');
        });
    },
    onFailure: (error) => {
      console.error('Google login failed', error);
    },
  });

  return (
    <OpacityContainer>
      <OutInSideClick handleClickOutside={closePopup}>
        <PopupContainer>
          <StyledCloseIcon src={closeIcon} alt="close" onClick={closePopup} />
          <Title>{t('Connexion')}</Title>
          <CustomerForm
            dataCy="popup-"
            inputsList={emailOrPhoneForm}
            listName="emailOrPhoneForm"
            checkFormInputValue={checkFormInputValue}
            setFormInputValue={setFormInputValue}
            compact
            customerEmail={customerEmail}
          />
          {isLoading && <LoaderIcon style={{ width: '25px', height: '25px' }} />}
          <Button black marginTop isActive={!isLoading} onClick={isLoading ? null : askForTemporaryPassword} data-cy="next">{t('Suivant')}</Button>
          {!isFromOrderDetails ? (
            <>
              <Span>{t('OU')}</Span>
              <Button black isActive onClick={() => loginWithGoogle()}>{t('Se connecter avec Google')}</Button>
            </>
          ) : null}
        </PopupContainer>
        {(isTemporaryPasswordSent || !isAlreadyCustomer) && (
          <PopupLogin
            emailOrPhone={emailOrPhone}
            closePopup={closePopup}
            isAlreadyCustomer={isAlreadyCustomer}
            order={order}
            isFromOrderDetails={isFromOrderDetails}
          />
        )}
      </OutInSideClick>
    </OpacityContainer>
  );
}

PopupTemporaryPassword.propTypes = {
  closePopup: PropTypes.func.isRequired,
  orderContext: PropTypes.shape({
    country: PropTypes.shape({}),
  }).isRequired,
  customerContext: PropTypes.shape({
    setCustomerAndGetOrders: PropTypes.func.isRequired,
  }).isRequired,
  isFromOrderDetails: PropTypes.bool,
  customerEmail: PropTypes.string,
  order: PropTypes.shape({}),
};

PopupTemporaryPassword.defaultProps = {
  isFromOrderDetails: false,
  customerEmail: undefined,
  order: {},
};

export default withNavigation(
  withCustomerContext(
    withOrderContext(PopupTemporaryPassword),
  ),
);
